// A single recurring availability time slot
export interface AvailabilitySlot {
  id: string; // A unique ID for the entire series (e.g., a timestamp or UUID)
  start: number; // In minutes from midnight (e.g., 600 for 10:00 AM)
  end: number; // In minutes from midnight (e.g., 720 for 12:00 PM)
  repeat: 'Daily' | 'Weekly' | 'Every weekday (Mon – Fri)';
}

// The main availability object on the user's profile
export interface AvailabilityObject {
  sunday: AvailabilitySlot[];
  monday: AvailabilitySlot[];
  tuesday: AvailabilitySlot[];
  wednesday: AvailabilitySlot[];
  thursday: AvailabilitySlot[];
  friday: AvailabilitySlot[];
  saturday: AvailabilitySlot[];
}
// src/types/schemas/abc.ts
  export class UserDetails {
    id!: string;
    name?: string;
    email?: string;
    account_verified?: boolean;
    isProfileComplete?: boolean;
    isAdmin?: boolean;

  
    profilePhotoUrl?: string;
    proofFileUrl?: string;
    CVFileUrl?: string; 
  
    subjects?: string[];
    levels?: string[];
    expertise?: Record<string, string[]>;
    hourlyRate?: number;
  
    university?: 'Oxford' | 'Cambridge';
    college?: string;
    degreeCourse?: string;
    gradYear?: number;

    qualificationGroups?: QualificationGroup[]; 
    classification?: string;
    description?: string;
    sortCode?: string; 
    accountHolderName?: string; 
    accountNumber?: string;
    role!: 'tutor' | 'parent'; // Use a strict type for better autocompletion and safety

    availability?: AvailabilityObject;
    timezone?: string;
    profileColor?: string;

    offersFreeIntroCall?: boolean;
    offersFreeFirstLesson?: boolean;

    email_verified?: boolean;
    discoverable?: boolean;
}

/* -------------------------------------------------------------------------- */
/*                                SUBJECTS                                    */
/* -------------------------------------------------------------------------- */
export const SUBJECT_OPTIONS = [
  'Maths',
  'Further Maths',
  'Biology',
  'Chemistry',
  'Physics',
  'Combined Science', // Key for GCSE
  'Computer Science', // Groups Programming, Data Science, etc.

  // --- Core Humanities & English ---
  'English Language',
  'English Literature',
  'History',
  'Geography',

  // --- Social Sciences ---
  'Economics',
  'Psychology',
  'Politics',
  'Law',
  'Philosophy',
  'Religious Studies',

  // --- Languages ---
  'French',
  'Spanish',
  'German',
  'Mandarin Chinese',
  'Classics (Latin/Greek)',
  'Russian',
   
  ] 
  
  /* -------------------------------------------------------------------------- */
  /*                                LEVELS                                      */
  /* -------------------------------------------------------------------------- */
  export const LEVEL_OPTIONS = [
    'GCSE or equivalent', 'A-Level or equivalent', 'IB or equivalent', 'SAT / ACT', 'AP',
    'Cambridge Admissions', 'Oxford Admissions',
    'Undergraduate',
    'Postgraduate (Masters)', 'Postgraduate (PhD)',


  ] 
  
  export type SubjectEntry = {
    id: string; // Unique ID for this specific row
    subject: string;
    grade: string;
  };
  
  // A group of qualifications of the same type
  export type QualificationGroup = {
    id: string; // Unique ID for the entire group
    type: string; // 'A-Level', 'GCSE', etc.
    entries?: SubjectEntry[];
    score?: string;
  };

  export const oxfordColleges = [
    'Balliol',
    'Brasenose',
    'Christ Church',
    'Corpus Christi',
    'Exeter',
    'Green Templeton',
    'Harris Manchester',
    'Hertford',
    'Jesus',
    'Keble',
    'Kellogg',
    'Lady Margaret Hall',
    'Linacre',
    'Lincoln',
    'Magdalen',
    'Mansfield',
    'Merton',
    'New College',
    'Nuffield',
    'Oriel',
    'Pembroke',
    "The Queen's",  
    "Regent's Park",  
    "St Anne's",  
    "St Antony's",  
    "St Benet's Hall",  
    "St Catherine's",  
    "St Cross",  
    "St Edmund Hall",  
    "St Hilda's",  
    "St Hugh's",  
    "St John's",  
    "St Peter's",  
    'Somerville',
    'Trinity',
    'University College',
    'Wadham',
    'Worcester',
    'Wolfson',
  ]

  export const cambridgeColleges = [
    "Christ's",
    'Churchill',
    'Clare',
    'Clare Hall',
    'Corpus Christi',
    'Darwin',
    'Downing',
    'Emmanuel',
    'Fitzwilliam',
    'Girton',
    'Gonville & Caius',
    'Homerton',
    'Hughes Hall',
    'Jesus',
    "King's",
    'Lucy Cavendish',
    'Magdalene',
    'Murray Edwards',
    'Newnham',
    'Pembroke',
    'Peterhouse',
    "Queens'",
    'Robinson',
    "St Catharine's",
    "St Edmund's",
    "St John's",
    'Selwyn',
    'Sidney Sussex',
    'Trinity',
    'Trinity Hall',
    'Wolfson',
  ]

  export const classificationOptions = [
    'First Class Honours (1st)',
    'Upper Second-Class Honours (2:1)',
    'Lower Second-Class Honours (2:2)',
    'Third-Class Honours (3rd)',
    'Pass',
    'Distinction',
    'Merit',
]

export const ITEMS_PER_PAGE = 10;
